<template>
    <button v-bind="attrs" class="c-button">
        <slot />
    </button>
</template>

<script lang="ts" setup>
import { useAttrs } from 'vue'

const attrs: any = useAttrs()
</script>

<style lang="scss" scoped>
.c-button {
    display: block;
    padding: 8px 14px;
    background: var(--c-color-primary);
    border: 0;
    border-radius: var(--c-border-radius);
    color: #fff;
    text-align: center;
    cursor: pointer;
    outline: 0;
    width: auto;
    font-weight: 500;
    font-size: var(--c-font-size);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(:disabled) {
        background: #006663;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 122, 119, 0.25);
    }

    &:active:not(:disabled) {
        transform: translateY(0) scale(0.98);
        box-shadow: none;
    }
}

.border {
    background: #fff;
    color: var(--c-color-primary);
    border: 1px solid var(--c-color-primary);

    &:hover:not(:disabled) {
        background: var(--c-color-primary-light);
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 122, 119, 0.15);
    }

    &:active:not(:disabled) {
        transform: translateY(0) scale(0.98);
        background: var(--c-color-primary-light);
        box-shadow: none;
    }

    &.negative {
        background: none;
        color: #fff;
        border-color: #fff;
        &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.1);
        }
    }
}

.simple {
    background: #fff;
    color: var(--c-color-primary);
    border: none;

    &:hover:not(:disabled) {
        background: var(--c-color-bg);
    }

    &.negative {
        background: none;
        color: #fff;
        &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.1);
        }
    }
}

.bare {
    background: none;
    color: var(--c-color-primary);
    border: none;
    padding: 0;
    text-align: left;
    font-weight: 500;

    &:hover:not(:disabled) {
        text-decoration: underline;
        color: #006663;
        background: none;
        transform: none;
        box-shadow: none;
    }

    &:active:not(:disabled) {
        color: #005552;
        transform: none;
    }

    &.negative {
        color: #fff;
        &:hover:not(:disabled) {
            color: rgba(255, 255, 255, 0.8);
        }
    }
}

.disabled,
:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    pointer-events: none;
}

.alert {
    background: var(--c-color-alert);

    &:hover:not(:disabled) {
        background: #e04541;
        box-shadow: 0 2px 8px rgba(244, 89, 85, 0.3);
    }

    &.border {
        background: #fff;
        color: var(--c-color-alert);
        border: 1px solid var(--c-color-alert);
        &:hover:not(:disabled) {
            background: rgba(244, 89, 85, 0.08);
            box-shadow: 0 2px 8px rgba(244, 89, 85, 0.15);
        }
    }

    &.simple {
        background: #fff;
        color: var(--c-color-alert);
        &:hover:not(:disabled) {
            background: rgba(244, 89, 85, 0.08);
        }
    }

    &.bare {
        background: none;
        color: var(--c-color-alert);
        text-decoration: none;
        &:hover:not(:disabled) {
            color: #e04541;
            text-decoration: underline;
        }
    }
}

.warning {
    background: var(--c-color-warning);
    color: #5c4a00;

    &:hover:not(:disabled) {
        background: #e5b12e;
        box-shadow: 0 2px 8px rgba(250, 197, 51, 0.3);
    }

    &.border {
        background: #fff;
        color: #b08f00;
        border: 1px solid var(--c-color-warning);
        &:hover:not(:disabled) {
            background: rgba(250, 197, 51, 0.12);
        }
    }

    &.simple {
        background: #fff;
        color: #b08f00;
        &:hover:not(:disabled) {
            background: rgba(250, 197, 51, 0.12);
        }
    }

    &.bare {
        background: none;
        color: #b08f00;
        &:hover:not(:disabled) {
            color: #8a7000;
            text-decoration: underline;
        }
    }
}

.border-spin {
    position: relative;
    z-index: 2;
    overflow: hidden;
    background: #fff;
    color: var(--c-color-primary);

    &::before {
        position: absolute;
        content: '';
        left: 50%;
        top: 50%;
        width: 500px;
        height: 500px;
        animation: rotate 2s infinite linear;
        background: conic-gradient(from 0deg, var(--c-color-primary), var(--c-color-primary-light), var(--c-color-primary));
        transform-origin: 0 0;
        z-index: -1;
    }

    &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        right: 2px;
        bottom: 2px;
        background: #fff;
        z-index: -1;
        border-radius: calc(var(--c-border-radius) - 2px);
    }
}

@keyframes rotate {
    100% {
        transform: rotate(360deg);
    }
}
</style>
